import React, { useEffect, useState } from "react";
import { Alert, Spinner, Modal, Button } from "react-bootstrap";
import { useHistory } from "react-router-dom";
import { Icon } from "../../utils/iconHelper";
import { useEntityLabels } from "../../_context/EntityLabelsContext";
import ProgramManagementService, {
  ProgramListItem,
  ProgramLookups,
} from "../../_services/ProgramManagementService";
import ProgramFormPanel from "./components/ProgramFormPanel";
import { useDocumentTitle } from "../../hooks/useDocumentTitle";
import "./program-management.css";

const ProgramManagementPage: React.FC = () => {
  const { label, labelPlural } = useEntityLabels();
  const programSectionLabel = label("program", "Program Management");
  const programNoun = programSectionLabel.replace(/\s*Management\s*$/i, "") || "Program";
  useDocumentTitle(programSectionLabel);
  const history = useHistory();

  const [programs, setPrograms] = useState<ProgramListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 20;

  const [nameFilter, setNameFilter] = useState("");
  const [codeFilter, setCodeFilter] = useState("");
  const [schoolFilter, setSchoolFilter] = useState("");
  const [programTypeFilter, setProgramTypeFilter] = useState("");
  const [yearFilter, setYearFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("active");
  const [lookups, setLookups] = useState<ProgramLookups>({
    years: [], terms: [], courses: [], schools: [], programTypes: [],
    streams: [], testCategories: [], universities: [], learningOptions: [], disciplines: [],
  });

  const [showFormPanel, setShowFormPanel] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingProgram, setDeletingProgram] = useState<ProgramListItem | null>(null);

  useEffect(() => {
    loadLookups();
  }, []);

  useEffect(() => {
    fetchPrograms();
  }, [currentPage, statusFilter]);

  const loadLookups = async () => {
    const r = await ProgramManagementService.getLookups();
    if (r.success) setLookups(r.data);
  };

  const fetchPrograms = async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await ProgramManagementService.searchPrograms({
        page: currentPage,
        limit: pageSize,
        name: nameFilter || undefined,
        code: codeFilter || undefined,
        schoolId: schoolFilter ? Number(schoolFilter) : undefined,
        programTypeId: programTypeFilter ? Number(programTypeFilter) : undefined,
        yearId: yearFilter ? Number(yearFilter) : undefined,
        includeInactive: statusFilter === "all" || statusFilter === "inactive" ? true : undefined,
      });
      if (r.success) {
        let rows = r.data.programs;
        if (statusFilter === "inactive") rows = rows.filter((p) => p.enableStatus !== 1);
        setPrograms(rows);
        setTotalCount(r.data.pagination.total);
      } else {
        setError(`Failed to fetch ${labelPlural("program", "Program").toLowerCase()}`);
      }
    } catch (e: any) {
      setError(e.message || `Failed to fetch ${labelPlural("program", "Program").toLowerCase()}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => { setCurrentPage(1); fetchPrograms(); };
  const handleReset = () => {
    setNameFilter("");
    setCodeFilter("");
    setSchoolFilter("");
    setProgramTypeFilter("");
    setYearFilter("");
    setStatusFilter("active");
    setCurrentPage(1);
  };

  const confirmDelete = async () => {
    if (!deletingProgram) return;
    const r = await ProgramManagementService.deleteProgram(deletingProgram.id);
    if (r.success) {
      setSuccess(`${programNoun} "${deletingProgram.name}" deactivated`);
      setShowDeleteModal(false);
      setDeletingProgram(null);
      fetchPrograms();
    } else {
      setError(r.message || "Failed to delete program");
    }
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  return (
    <div className="pm-page">
      <div className="pm-header">
        <h1>{programSectionLabel}</h1>
        <button className="pm-btn-create" onClick={() => { setEditingId(null); setShowFormPanel(true); }}>
          <Icon.Plus size={16} /> Create New {programNoun}
        </button>
      </div>

      {error && (
        <div className="pm-alert pm-alert-error">
          <span>{error}</span>
          <button className="pm-alert-dismiss" onClick={() => setError(null)}>&times;</button>
        </div>
      )}
      {success && (
        <div className="pm-alert pm-alert-success">
          <span>{success}</span>
          <button className="pm-alert-dismiss" onClick={() => setSuccess(null)}>&times;</button>
        </div>
      )}

      <div className="pm-filters" style={{ flexDirection: "column", alignItems: "stretch", gap: 8 }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: 12 }}>
          <div>
            <label className="form-label small fw-medium d-block mb-1">Name</label>
            <input
              className="pm-search-input"
              type="text"
              value={nameFilter}
              onChange={(e) => setNameFilter(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              style={{ width: "100%" }}
            />
          </div>
          <div>
            <label className="form-label small fw-medium d-block mb-1">Code</label>
            <input
              className="pm-search-input"
              type="text"
              value={codeFilter}
              onChange={(e) => setCodeFilter(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              style={{ width: "100%" }}
            />
          </div>
          <div>
            <label className="form-label small fw-medium d-block mb-1">{label("school", "BU")}</label>
            <select
              className="pm-filter-select"
              value={schoolFilter}
              onChange={(e) => setSchoolFilter(e.target.value)}
              style={{ width: "100%" }}
            >
              <option value="">-- Select {label("school", "BU")} --</option>
              {lookups.schools.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="form-label small fw-medium d-block mb-1">{label("program", "Program")} Type</label>
            <select
              className="pm-filter-select"
              value={programTypeFilter}
              onChange={(e) => setProgramTypeFilter(e.target.value)}
              style={{ width: "100%" }}
            >
              <option value="">-- select --</option>
              {lookups.programTypes.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="form-label small fw-medium d-block mb-1">{label("year", "Intake")}</label>
            <select
              className="pm-filter-select"
              value={yearFilter}
              onChange={(e) => setYearFilter(e.target.value)}
              style={{ width: "100%" }}
            >
              <option value="">-- Select {label("year", "Intake")} --</option>
              {lookups.years.map((y) => (
                <option key={y.id} value={y.id}>{y.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="form-label small fw-medium d-block mb-1">Status</label>
            <select
              className="pm-filter-select"
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
              style={{ width: "100%" }}
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="all">All</option>
            </select>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span style={{ color: "var(--text-muted)", fontSize: 12 }}>
            💡 <code>%</code> or <code>*</code> can be used as a wildcard while searching on the Name
          </span>
          <div style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "center" }}>
            <button className="pm-btn-action primary" onClick={handleSearch}>Search</button>
            <button className="pm-btn-action" onClick={handleReset}>Reset</button>
            <span style={{ color: "var(--text-muted)", fontSize: 14 }}><strong>{totalCount}</strong> {labelPlural("program", "Program").toLowerCase()}</span>
          </div>
        </div>
      </div>

      <div className="pm-table-container">
        {loading ? (
          <div className="pm-loading">
            <Spinner role="status" animation="border" variant="primary" />
            <p>Loading {labelPlural("program", "Program").toLowerCase()}...</p>
          </div>
        ) : programs.length === 0 ? (
          <div className="pm-empty">
            <Icon.Book size={48} style={{ color: "var(--text-faint)" }} />
            <h5>No {labelPlural("program", "Program").toLowerCase()} found</h5>
            <p>Try adjusting filters or create a new {programNoun.toLowerCase()}.</p>
          </div>
        ) : (
          <>
            <table className="pm-table">
              <thead>
                <tr>
                  <th style={{ width: 60 }}>ID</th>
                  <th>Name</th>
                  <th style={{ width: 140 }}>Short Code</th>
                  <th style={{ width: 110 }}>Years</th>
                  <th style={{ width: 110 }}>Intakes</th>
                  <th style={{ width: 110 }}>{labelPlural("term", "Term")}</th>
                  <th style={{ width: 110 }}>Reg. Open</th>
                  <th style={{ width: 100 }}>Status</th>
                  <th style={{ width: 160, textAlign: "right" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {programs.map((p) => (
                  <tr key={p.id}>
                    <td className="pm-cell-muted">{p.id}</td>
                    <td>
                      <span className="pm-program-name" onClick={() => history.push(`/program-management/${p.id}`)}>
                        {p.name || "-"}
                      </span>
                    </td>
                    <td className="pm-cell-muted">{p.programShortCode || "—"}</td>
                    <td className="pm-cell-muted">{p.noOfAcademicYears ?? "—"}</td>
                    <td className="pm-cell-muted">{p.intakeCount}</td>
                    <td className="pm-cell-muted">{p.termCount}</td>
                    <td className="pm-cell-muted">{p.openForRegistration === 1 ? "Yes" : "No"}</td>
                    <td>
                      {p.enableStatus === 1
                        ? <span className="pm-status-active">Active</span>
                        : <span className="pm-status-inactive">Inactive</span>}
                    </td>
                    <td style={{ textAlign: "right" }}>
                      <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                        <button className="pm-btn-action" onClick={() => history.push(`/program-management/${p.id}`)} title="View">
                          <Icon.Eye size={14} />
                        </button>
                        <button className="pm-btn-action" onClick={() => { setEditingId(p.id); setShowFormPanel(true); }} title="Edit">
                          <Icon.Edit2 size={14} />
                        </button>
                        <button className="pm-btn-action" onClick={() => { setDeletingProgram(p); setShowDeleteModal(true); }} title="Deactivate">
                          <Icon.Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {totalPages > 1 && (
              <div className="pm-pagination">
                <span className="pm-cell-muted" style={{ fontSize: 13 }}>
                  Page {currentPage} of {totalPages}
                </span>
                <div style={{ display: "flex", gap: 8 }}>
                  <button className="pm-btn-page" disabled={currentPage <= 1} onClick={() => setCurrentPage((p) => p - 1)}>Previous</button>
                  <button className="pm-btn-page" disabled={currentPage >= totalPages} onClick={() => setCurrentPage((p) => p + 1)}>Next</button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <ProgramFormPanel
        show={showFormPanel}
        onHide={() => setShowFormPanel(false)}
        programId={editingId}
        lookups={lookups}
        onSuccess={() => {
          setShowFormPanel(false);
          setSuccess(editingId ? `${programNoun} updated successfully` : `${programNoun} created successfully`);
          setEditingId(null);
          fetchPrograms();
        }}
      />

      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton><Modal.Title>Confirm Deactivation</Modal.Title></Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to deactivate this {programNoun.toLowerCase()}?</p>
          {deletingProgram && (
            <Alert variant="warning" className="mb-0">
              <strong>{deletingProgram.name}</strong>
              {deletingProgram.programShortCode && <><br /><small className="text-muted">Code: {deletingProgram.programShortCode}</small></>}
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>Cancel</Button>
          <Button variant="danger" onClick={confirmDelete}>Deactivate</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ProgramManagementPage;
